import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { ProductNames } from '@xbs/xbs-enums';
import { Observable, from } from 'rxjs';

import { SessionService, PlatformService } from '../../shared/services';
import { Container } from '../../shared/models';
import { HeaderConstants } from '../../shared/constants';
import { AppConfig } from '../../shared/services/app-config';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  IdentityInfo: any;

  constructor(
    private _sessionService: SessionService,
    private _appConfig: AppConfig,
    private _platformService: PlatformService,
  ) {
    this.IdentityInfo = this._platformService.getPlatformModule().IdentityInfo;
  }

  // TODO: Would be more declarative if we had e.g. configService.getTpiqAuthStack().shouldAppendContainer, .shouldProxyRequest, etc;
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.handle(req, next));
  }

  async handle(req: HttpRequest<any>, next: HttpHandler) {
    req = this.appendAuthenticationToken(req);
    req = this.appendXbsProduct(req);
    req = await this.getProxiedRequest(req);
    req = this.appendContainerToRequest(req);

    return next.handle(req).toPromise();
  }

  private async getProxiedRequest(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    const authStackUrl = this._appConfig.getAuthUrl();

    const isHttps: boolean = req.url.startsWith('https://');
    const isAuthenticated: boolean = !!this._sessionService.getAuthToken();
    const isAuthStack: boolean = req.url.includes(authStackUrl);
    const isS3Call: boolean = req.url.includes('s3.amazonaws.com');
    const isSelfCall: boolean = req.url.includes(window.location.host);
    const shouldProxyRequest: boolean = isHttps && isAuthenticated && !isAuthStack && !isS3Call && !isSelfCall;

    if (shouldProxyRequest) {
      const proxyUrl = this._appConfig.getProxyUrl();
      const url = `${proxyUrl}/v1/http?redirectUrl=${encodeURIComponent(req.url)}`;
      req = req.clone({ url });
      req = await this.appendXbsHeaders(req);
    }

    return req;
  }

  private appendContainerToRequest(req: HttpRequest<any>): HttpRequest<any> {
    const shouldAppendContainer: boolean = req.url.search(/login/gi) === -1 &&
      req.url.search(/principle-api/gi) === -1 &&
      req.url.search(/containers/gi) === -1 &&
      req.url.search(/tpsafe/gi) === -1 &&
      req.url.search(/upload/gi) === -1 &&
      req.url.search(/auth/gi) === -1 &&
      (req.method === 'POST' || req.method === 'PATCH' || req.method === 'PUT');

    if (shouldAppendContainer) {
      const container: Container = this._sessionService.getWorkingContainer();

      Object.assign(container, req.body.container);
      req.body.container = container;
    }

    return req;
  }

  private appendAuthenticationToken(req: HttpRequest<any>): HttpRequest<any> {
    const authenticationToken: string = this._sessionService.getAuthToken();
    const isNoAmzAlgorithm: boolean = req.url.search(/X-Amz/gi) === -1;
    const isAuthenticated: boolean = !!authenticationToken && isNoAmzAlgorithm;

    if (isAuthenticated) {
      const headers: HttpHeaders = req.headers.set(HeaderConstants.AUTHORIZATION, 'Bearer ' + authenticationToken);
      req = req.clone({ headers });
    }

    return req;
  }

  private appendXbsProduct(req: HttpRequest<any>): HttpRequest<any> {
    const productId: string = ProductNames.ByName.Tpiq.Id;
    const productUuid: string = ProductNames.ByName.Tpiq.Uuid;
    let headers: HttpHeaders = req.headers.set(HeaderConstants.XBS_PRODUCT_ID, productId).set(HeaderConstants.XBS_PRODUCT_UUID, productUuid);

    req = req.clone({ headers });

    return req;
  }

  /* eslint-disable no-unused-expressions */
  private async appendXbsHeaders(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    const session = await this.IdentityInfo.getIdentitySession();
    const containerUuid: string = session.containerUuid;
    const roleUuid: string = session.roleUuid;
    const userUuid: string = session.userUuid;
    const pageUrl: string = window.location.href || '';

    let headers: HttpHeaders = req.headers;
    containerUuid && (headers = headers.set(HeaderConstants.XBS_CONTAINER_UUID, containerUuid));
    roleUuid && (headers = headers.set(HeaderConstants.XBS_ROLE_UUID, roleUuid));
    userUuid && (headers = headers.set(HeaderConstants.XBS_USER_UUID, userUuid));
    headers = headers.set(HeaderConstants.XBS_IS_NEW_UI, 'false');
    headers = headers.set(HeaderConstants.XBS_ORIGIN_URL, pageUrl);
    req = req.clone({ headers });
    return req;
  }
}
